<template>
    <DCard padding>
        <DRow>
            <DRow
                class="mb-3"
                align-horizontal="start"
                align-vertical="end"
            >
                <DCol
                    sm="3"
                    class="pt-1"
                >
                    <DSelect
                        id="status-dropdown"
                        v-model="filterData.status"
                        :options="statusOptions"
                        :label="__('contracts.contract_upload.filters.status')"
                    />
                </DCol>
                <DCol
                    sm="3"
                    class="d-flex align-items-end gap-2"
                >
                    <DButton
                        id="filter-apply"
                        :label="__('messages.actions.filter')"
                        @click="changePage"
                    />
                    <DButton
                        id="filter-reset"
                        :label="__('messages.actions.reset_filter')"
                        @click="resetFilters"
                    />
                </DCol>
            </DRow>
            <DCol>
                <DTable
                    class="table-responsive"
                    :items="items"
                    :fields="fields"
                    hover
                >
                    <template #customer="{item}">
                        <td v-if="userPermissions.includes('customer.view')">
                            <Link :href="customerProfileRoute(item.customer.id)">
                                {{ item.customerFullName }} ({{ item.customer.id }})
                            </Link>
                        </td>
                        <td v-else>
                            {{ item.customerFullName }}
                        </td>
                    </template>
                    <template #contract="{item}">
                        <td v-if="userPermissions.includes('contract.view') && item.status === 'success' && item.contract_id !== null">
                            <Link :href="contractRoute(item)">
                                {{ item.contract }} ({{ item.contract_id }})
                            </Link>
                        </td>
                        <td v-else>
                            {{ item.contract }}
                        </td>
                    </template>
                    <template #status="{item}">
                        <td>
                            <DTag
                                :label="__('contracts.contract_upload.statuses.' + item.status)"
                                :color="getStatusTagColor(item.status)"
                            />
                        </td>
                    </template>
                </DTable>
                <DPagination
                    v-if="paginationData.last_page >= 1"
                    :active-page="paginationData.current_page"
                    :pages="paginationData.last_page"
                    align="center"
                    @update:activePage="changePage"
                />
            </DCol>
        </DRow>
        <DFabs>
            <DFab
                v-if="items.length > 0 && items[0].status === 'draft'"
                id="btn-apply-contract-upload"
                icon="content-save"
                color="primary"
                :label="__('contracts.contract_upload.messages.apply')"
                @click="submitContractUpload"
            />
            <DFab
                v-if="items.length > 0 && items[0].status === 'draft'"
                id="btn-show-cancel-contract-upload"
                icon="close"
                color="danger"
                :label="__('contracts.contract_upload.messages.abort')"
                @click="cancelContractUpload"
            />
            <DFab
                id="btn-preview-new-contract-upload"
                icon="arrow-left"
                color="primary"
                :label="__('contracts.contract_upload.messages.back')"
                @click="contractsUploadRoute"
            />
        </DFabs>
    </DCard>
</template>

<script setup>
import { route } from '~@/Composables/route'
import { router, usePage } from '@inertiajs/vue3'
import { __ } from '~@/Composables/translation'
import { computed, ref } from 'vue'

const props = defineProps({
    contractCsvFile: {
        type: Object,
        default: () => {
            return {}
        }
    },
    paginationData: {
        type: Object,
        default: () => {
            return {}
        }
    },
    items: {
        type: Array,
        default: () => {
            return []
        }
    },
    statusOptions: {
        type: Array,
        default: () => {
            return []
        }
    },
    filters: {
        type: Object,
        default: () => {
            return {}
        }
    }
})

const fields = [
    { key: 'id', label: __('contracts.contract_upload.headers.id') },
    { key: 'customer', label: __('contracts.contract_upload.headers.customer') },
    { key: 'contract', label: __('contracts.contract_upload.headers.contract') },
    { key: 'product', label: __('contracts.contract_upload.headers.product') },
    { key: 'product_plan_id', label: __('contracts.contract_upload.headers.product_plan_id') },
    { key: 'addon_product1_id', label: __('contracts.contract_upload.headers.addon_product1_id') },
    { key: 'addon_product1_plan_id', label: __('contracts.contract_upload.headers.addon_product1_plan_id') },
    { key: 'addon_product2_id', label: __('contracts.contract_upload.headers.addon_product2_id') },
    { key: 'addon_product2_plan_id', label: __('contracts.contract_upload.headers.addon_product2_plan_id') },
    { key: 'internal_note', label: __('contracts.contract_upload.headers.internal_note') },
    { key: 'external_id', label: __('contracts.contract_upload.headers.external_id') },
    { key: 'status', label: __('contracts.contract_upload.headers.status') },
    { key: 'date', label: __('contracts.contract_upload.headers.date') }
]

const customerProfileRoute = (id) => route('customers.customers.show', id)

const contractsUploadRoute = () => router.get(route('contracts.contract-upload.index'))

const submitContractUpload = () => router.get(route('contracts.contract-upload.submit', props.contractCsvFile.id))

const cancelContractUpload = () => router.get(route('contracts.contract-upload.cancel', props.contractCsvFile.id))

const userPermissions = computed(() => usePage().props.auth.permissions)

const contractRoute = (value) => {
    return route('customers.customers.contracts.show', {
        customer: value.customer.id,
        contract: value.contract_id
    })
}

const filterData = ref({
    status: props.filters.status ?? null
})

const getStatusTagColor = (status) => {
    if (status === 'draft') {
        return 'primary'
    }
    if (status === 'open' || status === 'not_sent') {
        return 'secondary'
    }
    if (status === 'success' || status === 'sent') {
        return 'success'
    }
    return 'danger'
}

const resetFilters = () => {
    filterData.value.status = null
    changePage()
}

const changePage = (page) => {
    const query = Object.fromEntries(Object.entries(filterData.value).filter(([_, v]) => v))
    if (page > 1) {
        query.page = page
    }

    router.get(route('contracts.contract-upload.show', { contractCsvFile: props.contractCsvFile.id }), query)
}

</script>

<style scoped lang="scss">

</style>
